import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useSafeLocalStorage as useLocalStorage } from '@gonfalon/observability-ui/hooks/useSafeLocalStorage';
import { useLatestRef } from '@gonfalon/react/useLatestRef';

import { clearConversationFromStorage } from './clearConversationFromStorage';
import { chromeForHistoryOpen, firstUserMessageText, nextSavedConversationTitle } from './conversationTitle';
import { inferContextType } from './resources';
import type { ExternalSourceType, Resource, ResourceOrQuery } from './resources';

export type { ExternalSourceType, Resource, ResourceOrQuery } from './resources';

const DEFAULT_PANEL_WIDTH = 50;
const DEFAULT_MAX_PANEL_WIDTH = 85;
const LOCAL_STORAGE_WIDTH_KEY = 'agent-insights-panel-width';

export const PERMISSION_MODE_READ = 'read';
export const PERMISSION_MODE_READ_WRITE = 'read-write';
export type PermissionMode = typeof PERMISSION_MODE_READ | typeof PERMISSION_MODE_READ_WRITE;

// Seeds the prompt composer's "automated rollout" mode: opening with this turns the mode on and
// preselects the given environments and per-env release type ('simple' = immediate, 'policy' = use
// the release policy), so a host can hand off a proposed rollout for the user to review and edit.
export interface AutomatedRolloutSeed {
  environments: Array<{ environmentKey: string; releaseType: 'simple' | 'policy' }>;
}

export interface OpenParams {
  initialQuery?: string;
  // This pulls the most recent conversation from session storage if true
  // if false, it will start a new conversation
  // Default behavior: false (new conversation) when context is provided, true otherwise
  persistConversation?: boolean;
  // If provided, will load this specific conversation in read-only mode
  conversationIdToLoad?: string;
  // If provided, auto-selects matching Vega repositories (matched by name against GitHub integration repos)
  preferredRepositories?: string[];
  // When true, automatically share the conversation once created
  shareConversation?: boolean;
  // When true, the agent is opened from an insights dashboard (read-only, no graph editing)
  isInsightsDashboard?: boolean;
  // Client-side request kind (e.g. 'Investigate', 'FlagCleanup').
  // Used only for analytics labels — it is NOT sent to the backend (the GraphQL
  // surface uses permission_mode). Plain string rather than the retired
  // AgentRequestType enum.
  requestType?: string;
  context: {
    resource?: Resource;
    resourceOrQuery: ResourceOrQuery[];
  };
  // When set, opens the composer in automated-rollout mode with these environments preselected.
  automatedRollout?: AutomatedRolloutSeed;
}

export interface ConversationData {
  conversationId: string;
  status: string;
  // Failure reason from conversation_history — only present when status is
  // "failed" (e.g. "budget_exceeded: run exceeded its token budget").
  error?: string | null;
  /** Model-generated title when the history list already has one. */
  title?: string | null;
  messages: Array<{
    id: string;
    conversation_id: any;
    role: string;
    content: any;
    turn: number;
    tokens_used?: number | null;
    is_final: boolean;
    created_at: any;
  }>;
}

export interface AgentInsightsContextType {
  initialQuery: string;
  persistConversation: boolean;
  resource: Resource | undefined;
  preferredRepositories: string[];
  /** Set on the most recent `open`/`configureConversation`; the composer applies it once to seed automated-rollout mode. */
  automatedRolloutSeed: AutomatedRolloutSeed | undefined;
  context: {
    resourceOrQuery: ResourceOrQuery[];
  };
  isOpen: boolean;
  currentConversationId: string | null;
  conversationIdToLoad: string | null;
  permissionMode: PermissionMode;
  setPermissionMode: (value: PermissionMode) => void;
  // Client-side request kind for FE routing/gating/analytics only (not sent to
  // the backend). See OpenParams.requestType.
  requestType: string;
  open: (params: OpenParams) => void;
  /** Set the conversation config (id, persistence, context, …) without opening the slide-out panel. For inline surfaces like the full-page conversation view. */
  configureConversation: (params: OpenParams) => void;
  close: () => void;
  clear: (options?: { preserveContext?: boolean }) => void;
  clearContext: () => void;
  addContextItem: (item: ResourceOrQuery) => void;
  removeContextItem: (index: number) => void;
  removeContextItemById: (id: string, type?: Resource) => void;
  loadConversation: (data: ConversationData) => void;
  /**
   * Apply history-row chrome immediately. Closing the history list would
   * otherwise flash the previous title until conversation_history returns.
   */
  adoptConversationChrome: (conversationId: string, title?: string | null) => void;
  clearRetrievedConversationData: () => void;
  retrievedConversationData: ConversationData | null;
  /**
   * Model-generated title for the open thread. Kept separate from
   * `retrievedConversationData` so it survives `clearRetrievedConversationData`
   * (that snapshot is dropped when the user continues a loaded run).
   */
  savedConversationTitle: string | null;
  /** First user message text for the live thread — used as a title fallback. */
  firstUserMessagePreview: string;
  setFirstUserMessagePreview: (preview: string) => void;
  setCurrentConversationId: (id: string | null) => void;
  /**
   * Discard the open conversation everywhere at once: provider state, the sessionStorage snapshot
   * a remount would restore from, and the local messages of a mounted `AgentInsights`. `clear`
   * only does the first, so a caller that keeps the conversation view mounted needs this instead.
   */
  startNewConversation: () => void;
  startConversationWithSource: (url: string, sourceType: ExternalSourceType, opts?: { initialQuery?: string }) => void;
  /** Register a serializer returning the current conversation as a markdown transcript. Set by the active conversation view; pass null on unmount. */
  registerConversationText: (getter: (() => string) | null) => void;
  /** Returns the current conversation as a markdown transcript, or '' when no conversation view is mounted. */
  getConversationText: () => string;
  panelWidth: number;
  setPanelWidth: (width: number) => void;
  maxPanelWidth: number;
  setMaxPanelWidth: (width: number) => void;

  isDraggingPanel: boolean;
  setIsDraggingPanel: (value: boolean) => void;
  livePanelWidth: number;
  setLivePanelWidth: (width: number) => void;
  shareConversation: boolean;
  graphMutationSignal: number;
  notifyGraphMutation: () => void;
  /**
   * Increments each time the user sends a prompt. Surfaces that show
   * conversation activity (e.g. the sidebar's "Most recent chats", ordered by
   * last user prompt) watch this to refetch and re-sort without a page reload.
   */
  conversationActivitySignal: number;
  notifyConversationActivity: () => void;
  /** Increments each time the conversation should be reset locally (e.g. by `startConversationWithSource`). */
  conversationClearSignal: number;
  isInsightsDashboard: boolean;
}

const contextItemKey = (resource: ResourceOrQuery): string => `${inferContextType(resource)}:${resource.id}`;

/**
 * Build a regex-friendly ID from an external-source URL. `extractContextFromPrompt`
 * accepts `[\w./-]+` for resource IDs, so we slugify the URL while keeping the
 * full URL in `payload.url` for everything that needs the original.
 */
const externalSourceIdFromUrl = (url: string): string => {
  const slug = url
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return slug || 'external-source';
};

const AgentInsightsContext = createContext<AgentInsightsContextType | undefined>(undefined);

export interface AgentInsightsProviderProps {
  children: ReactNode;
}

export const AgentInsightsProvider: React.FC<AgentInsightsProviderProps> = ({ children }) => {
  const [initialQuery, setInitialQuery] = useState<string>('');
  const [resourceOrQuery, setResourceOrQuery] = useState<ResourceOrQuery[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [resource, setResource] = useState<Resource | undefined>(undefined);
  const [persistConversation, setPersistConversation] = useState<boolean>(true);
  const [retrievedConversationData, setRetrievedConversationData] = useState<ConversationData | null>(null);
  const [savedConversationTitle, setSavedConversationTitle] = useState<string | null>(null);
  const [firstUserMessagePreview, setFirstUserMessagePreview] = useState('');
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [conversationIdToLoad, setConversationIdToLoad] = useState<string | null>(null);
  const [preferredRepositories, setPreferredRepositories] = useState<string[]>([]);
  const [automatedRolloutSeed, setAutomatedRolloutSeed] = useState<AutomatedRolloutSeed | undefined>(undefined);
  const [permissionMode, setPermissionMode] = useLocalStorage<PermissionMode>(
    'vega-permission-mode',
    PERMISSION_MODE_READ,
  );
  // Client-side request kind (string, not the retired AgentRequestType enum).
  // Drives analytics labels only; never sent to the backend.
  const [requestType, setRequestType] = useLocalStorage<string>('vega-ai-request-type-2', 'Investigate');
  const requestTypeRef = useLatestRef(requestType);
  const currentConversationIdRef = useLatestRef(currentConversationId);
  const savedConversationTitleRef = useLatestRef(savedConversationTitle);
  const [panelWidth, setPanelWidth] = useLocalStorage(LOCAL_STORAGE_WIDTH_KEY, DEFAULT_PANEL_WIDTH);
  const [maxPanelWidth, setMaxPanelWidth] = useState(DEFAULT_MAX_PANEL_WIDTH);

  const [isDraggingPanel, setIsDraggingPanel] = useState(false);
  const [livePanelWidth, setLivePanelWidth] = useState(DEFAULT_PANEL_WIDTH);
  const [shareConversation, setShareConversation] = useState(false);
  const [graphMutationSignal, setGraphMutationSignal] = useState(0);
  const [conversationActivitySignal, setConversationActivitySignal] = useState(0);
  const [conversationClearSignal, setConversationClearSignal] = useState(0);
  const [isInsightsDashboard, setIsInsightsDashboard] = useState(false);

  const notifyGraphMutation = useCallback(() => {
    setGraphMutationSignal((prev) => prev + 1);
  }, []);

  // The full-conversation "Copy conversation" control lives in the layout header
  // (next to Share), but the rendered conversation components live in
  // AgentInsights. The active conversation view registers a serializer here so
  // the header can produce a transcript on demand without lifting conversation
  // state up. A ref (not state) avoids re-rendering every consumer on each turn.
  const conversationTextGetterRef = useRef<(() => string) | null>(null);
  const registerConversationText = useCallback((getter: (() => string) | null) => {
    conversationTextGetterRef.current = getter;
  }, []);
  const getConversationText = useCallback(() => conversationTextGetterRef.current?.() ?? '', []);

  const notifyConversationActivity = useCallback(() => {
    setConversationActivitySignal((prev) => prev + 1);
  }, []);

  // Set the conversation config without touching panel visibility. Inline surfaces
  // (the full-page conversation view) use this directly; `open` layers the panel on top.
  const configureConversation = useCallback(
    (params: OpenParams) => {
      setInitialQuery(params.initialQuery ?? '');
      setResourceOrQuery(params.context.resourceOrQuery);
      setResource(params.context.resource);
      const shouldPersist = params.persistConversation !== undefined ? params.persistConversation : true;
      setPersistConversation(shouldPersist);
      setConversationIdToLoad(params.conversationIdToLoad ?? null);
      setPreferredRepositories(params.preferredRepositories ?? []);
      setAutomatedRolloutSeed(params.automatedRollout);
      setShareConversation(params.shareConversation ?? false);
      setIsInsightsDashboard(params.isInsightsDashboard ?? false);

      // Title chrome is independent of retrievedConversationData. Starting a
      // new thread (`persistConversation: false`) or switching to a different
      // saved id must drop the previous model title; otherwise the panel header
      // keeps showing that name after local messages have been cleared.
      // Re-opening the same id must keep the title: conversationIdToLoad is
      // unchanged, so the panel fetch effect does not re-run and would never
      // restore chrome via loadConversation.
      if (!shouldPersist) {
        setRetrievedConversationData(null);
        setSavedConversationTitle(null);
        setFirstUserMessagePreview('');
        currentConversationIdRef.current = null;
        setCurrentConversationId(null);
        setConversationClearSignal((prev) => prev + 1);
      } else if (params.conversationIdToLoad && params.conversationIdToLoad !== currentConversationIdRef.current) {
        setSavedConversationTitle(null);
        setFirstUserMessagePreview('');
      }

      if (params.requestType) {
        setRequestType(params.requestType);
      } else {
        // No explicit kind: when opening with context, clear any stale
        // creation/cleanup kind so a follow-up isn't mislabeled. Plain string
        // literals (enum retired).
        const hasContext = params.context.resourceOrQuery.length > 0;
        if (
          hasContext &&
          (requestTypeRef.current === 'FlagCleanup' ||
            requestTypeRef.current === 'GraphCreation' ||
            requestTypeRef.current === 'ExperimentCreation')
        ) {
          setRequestType('Investigate');
        }
      }
    },
    [setRequestType],
  );

  const open = useCallback(
    (params: OpenParams) => {
      configureConversation(params);
      setIsOpen(true);
    },
    [configureConversation],
  );

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const clear = useCallback((options?: { preserveContext?: boolean }) => {
    setInitialQuery('');
    if (!options?.preserveContext) {
      setResourceOrQuery([]);
    }
    setRetrievedConversationData(null);
    setSavedConversationTitle(null);
    setFirstUserMessagePreview('');
    currentConversationIdRef.current = null;
    setCurrentConversationId(null);
    setConversationIdToLoad(null);
  }, []);

  const startNewConversation = useCallback(() => {
    setInitialQuery('');
    setRetrievedConversationData(null);
    setSavedConversationTitle(null);
    savedConversationTitleRef.current = null;
    setFirstUserMessagePreview('');
    currentConversationIdRef.current = null;
    setCurrentConversationId(null);
    setConversationIdToLoad(null);
    // Both halves of the local state a mounted (or remounting) AgentInsights would otherwise
    // carry over: the snapshot it restores from, and the signal it resets its messages on.
    clearConversationFromStorage();
    setConversationClearSignal((prev) => prev + 1);
  }, []);

  const clearContext = useCallback(() => {
    setResourceOrQuery([]);
  }, []);

  const addContextItem = useCallback((item: ResourceOrQuery) => {
    setResourceOrQuery((prev) => {
      const key = contextItemKey(item);
      if (prev.some((existing) => contextItemKey(existing) === key)) {
        return prev;
      }
      return [...prev, item];
    });
  }, []);

  const removeContextItem = useCallback((index: number) => {
    setResourceOrQuery((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const removeContextItemById = useCallback((id: string, type?: Resource) => {
    setResourceOrQuery((prev) =>
      prev.filter((item) => {
        if (item.id !== id) {
          return true;
        }
        if (type && item.type !== type) {
          return true;
        }
        return false;
      }),
    );
  }, []);

  const loadConversation = useCallback((data: ConversationData) => {
    const previousConversationId = currentConversationIdRef.current;
    setRetrievedConversationData(data);
    setSavedConversationTitle((previousTitle) => {
      const nextTitle = nextSavedConversationTitle({
        incomingTitle: data.title,
        conversationId: data.conversationId,
        currentConversationId: previousConversationId,
        previousTitle,
      });
      savedConversationTitleRef.current = nextTitle;
      return nextTitle;
    });
    // Write through so a second history fetch in the same tick (the panel
    // conversationIdToLoad effect has no title) still sees this id.
    currentConversationIdRef.current = data.conversationId;
    setCurrentConversationId(data.conversationId);
    setFirstUserMessagePreview(firstUserMessageText(data.messages));
  }, []);

  const adoptConversationChrome = useCallback((conversationId: string, title?: string | null) => {
    const next = chromeForHistoryOpen({
      conversationId,
      currentConversationId: currentConversationIdRef.current,
      incomingTitle: title,
      previousTitle: savedConversationTitleRef.current,
    });
    setSavedConversationTitle(next.title);
    savedConversationTitleRef.current = next.title;
    if (next.clearPreview) {
      setFirstUserMessagePreview('');
    }
    if (next.adoptId) {
      currentConversationIdRef.current = conversationId;
      setCurrentConversationId(conversationId);
      // History unmounts AgentInsights. Drop the previous snapshot so remount
      // cannot rehydrate leftover user messages and write that prompt back.
      setRetrievedConversationData(null);
      clearConversationFromStorage();
      setConversationClearSignal((prev) => prev + 1);
    }
  }, []);

  const clearRetrievedConversationData = useCallback(() => {
    setRetrievedConversationData(null);
  }, []);

  const startConversationWithSource = useCallback(
    (url: string, sourceType: ExternalSourceType, opts?: { initialQuery?: string }) => {
      setInitialQuery(opts?.initialQuery ?? '');
      setRetrievedConversationData(null);
      setSavedConversationTitle(null);
      setFirstUserMessagePreview('');
      currentConversationIdRef.current = null;
      setCurrentConversationId(null);
      setConversationIdToLoad(null);
      setPersistConversation(false);
      setResourceOrQuery([
        {
          id: externalSourceIdFromUrl(url),
          type: 'ExternalSource',
          payload: { url, sourceType },
        },
      ]);
      setIsOpen(true);
      // Bump the signal so AgentInsights resets its local conversation state
      // even if `persistConversation` was already false (the dep-tracked effect
      // wouldn't re-fire on its own).
      setConversationClearSignal((prev) => prev + 1);
    },
    [],
  );

  const agentContext = useMemo(() => ({ resourceOrQuery }), [resourceOrQuery]);

  const value: AgentInsightsContextType = {
    initialQuery,
    persistConversation,
    resource,
    preferredRepositories,
    automatedRolloutSeed,
    context: agentContext,
    isOpen,
    currentConversationId,
    conversationIdToLoad,
    permissionMode,
    setPermissionMode,
    requestType,
    open,
    configureConversation,
    close,
    clear,
    clearContext,
    addContextItem,
    removeContextItem,
    removeContextItemById,
    loadConversation,
    adoptConversationChrome,
    clearRetrievedConversationData,
    retrievedConversationData,
    savedConversationTitle,
    firstUserMessagePreview,
    setFirstUserMessagePreview,
    setCurrentConversationId,
    shareConversation,
    startNewConversation,
    startConversationWithSource,
    registerConversationText,
    getConversationText,
    panelWidth,
    setPanelWidth,
    maxPanelWidth,
    setMaxPanelWidth,

    isDraggingPanel,
    setIsDraggingPanel,
    livePanelWidth,
    setLivePanelWidth,
    graphMutationSignal,
    notifyGraphMutation,
    conversationActivitySignal,
    notifyConversationActivity,
    conversationClearSignal,
    isInsightsDashboard,
  };

  return <AgentInsightsContext.Provider value={value}>{children}</AgentInsightsContext.Provider>;
};

export const useAgentInsights = (): AgentInsightsContextType => {
  const context = useContext(AgentInsightsContext);
  if (!context) {
    throw new Error('useAgentInsights must be used within an AgentInsightsProvider');
  }
  return context;
};
